import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith, tap } from 'rxjs/operators';
import { LocationSnapshotResponse, RhombusCameraSnapshotStatus, SnapshotCameraResponse, SnapshotItemResponse } from '../types';
import { LocationSnapshotsService } from './location-snapshots.service';
import { sortBySnapshotError } from '../secure/tabs/market-snapshots/market-snapshots.utils';

export type SnapshotImageState = {
  isLoading: boolean;
  error?: Error;
  value?: Blob;
};

export type SnapshotImage = {
  requestDateTime: string;
  url: string | null;
  cameraName: string;
  status: RhombusCameraSnapshotStatus;
  image$: Observable<SnapshotImageState>;
  blob?: Blob;
  snapshotError?: string;
};

export type LocationSnapshotWithImage = {
  locationId: number;
  locationName: string;
  branchId: number;
  cameraCount: number;
  status: RhombusCameraSnapshotStatus;
  score: number | null;
  previewUrl?: string | null;
  images: SnapshotImage[];
};

@Injectable({ providedIn: 'root' })
export class MarketSnapshotsService {
  private locationSnapshotsService = inject(LocationSnapshotsService);

  getBranchLocationSnapshots(branchId: number, locationIds: number[] | null, snapshotDate: Date): Observable<LocationSnapshotWithImage[]> {
    return this.locationSnapshotsService
      .getBranchLocationSnapshots(branchId, locationIds, snapshotDate)
      .pipe(
        map((data) =>
          data
            .filter(
              (location) =>
                (location.cameras?.length ?? 0) === 0 ||
                (location.snapshot_requests && location.snapshot_requests.some((req) => req.snapshots && req.snapshots.length > 0)),
            )
            .map((location) => this.toLocationSnapshot(location)),
        ),
      );
  }

  private toLocationSnapshot(location: LocationSnapshotResponse): LocationSnapshotWithImage {
    const allImages = location.snapshot_requests.flatMap((req) =>
      req.snapshots.map((snap) => this.toSnapshotImage(snap, req.request_date_time, req.status, location.cameras)),
    );
    sortBySnapshotError(allImages);
    const latestRequest = location.snapshot_requests?.[0];

    return {
      locationId: location.location_id,
      locationName: location.location_name,
      branchId: location.branch_id,
      cameraCount: location.cameras?.length || 0,
      previewUrl: latestRequest?.snapshots?.[0]?.url,
      status: latestRequest?.status ?? RhombusCameraSnapshotStatus.Unassigned,
      score: latestRequest?.score ?? null,
      images: allImages,
    };
  }

  private toSnapshotImage(
    snap: SnapshotItemResponse,
    requestDateTime: string,
    status: RhombusCameraSnapshotStatus,
    cameras: SnapshotCameraResponse[],
  ): SnapshotImage {
    const camera = (cameras || []).find((c) => c.location_camera_id === snap.location_camera_id);

    const imageObject: SnapshotImage = {
      url: snap.url?.trim() ?? null,
      cameraName: camera?.camera_name || 'Unknown Camera',
      requestDateTime,
      status,
      snapshotError: snap.snapshot_error,
      image$: of({ isLoading: true }),
    };

    if (snap.snapshot_error) {
      imageObject.image$ = of({ isLoading: false });
      return imageObject;
    }

    imageObject.image$ = this.locationSnapshotsService.getLocationSnapshot(imageObject.url!).pipe(
      map((value) => ({
        isLoading: false,
        value,
        error: undefined,
      })),
      catchError((error) =>
        of({
          isLoading: false,
          value: undefined,
          error,
        }),
      ),
      startWith({
        isLoading: true,
        value: undefined,
        error: undefined,
      }),
      tap((result) => {
        if (result.value) {
          imageObject.blob = result.value;
        }
      }),
    );

    return imageObject;
  }
}
